import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import _ from 'lodash';

import { COMMON_DATETIME_FORMAT } from 'constants/date';
import {
  HistoryTypeToPendingChangesTypeMap,
  PendingChangeKind,
  PendingChangesActionType,
} from 'constants/pendingChanges';
import {
  AI_ASSISTANT_AUTHOR_NAME,
  AI_PENDING_CHANGES_AUTHOR_KEY,
} from 'constants/pendingChangesAuthors';
import { getPersonInitials } from 'utils/loanApplication';
import { getPendingChangesAuthorLabel } from 'utils/pendingChangesAuthors';

import { normalizeLoanChangesValue } from '../LoanChangesHistory/LoanChangesHistory.utils';

import {
  ALL_SECTIONS_KEY,
  ChangeKindFilter,
  POLL_INTERVAL_MS,
  POLL_MAX_ATTEMPTS,
  PULSE_HIGHLIGHT_DURATION_MS,
  SECTION_PREFIX,
  SUBGROUP_PREFIX,
} from './PendingChangesDrawer.constants';

dayjs.extend(relativeTime);

export const getChangeLabel = count => (count !== 1 ? 'changes' : 'change');

export const withRecalculatedCount = (metaText, recalculatedCount) => {
  if (!recalculatedCount) {
    return metaText;
  }

  const valueLabel = recalculatedCount !== 1 ? 'values' : 'value';

  return `${metaText} · ${recalculatedCount} recalculated ${valueLabel}`;
};

export const getMetaText = (changeCount, userCount, recalculatedCount = 0) => {
  const userLabel = userCount !== 1 ? 'users' : 'user';

  return withRecalculatedCount(
    `${changeCount} ${getChangeLabel(changeCount)} by ${userCount} ${userLabel}`,
    recalculatedCount,
  );
};

export const getAuthorInitials = formattedFullName => {
  const nameParts = (formattedFullName || '').split(' ');

  return getPersonInitials({
    firstName: nameParts[0] || '',
    lastName: nameParts.slice(1).join(' ') || '',
  });
};

export const getChangeType = actionType => {
  const mapped = HistoryTypeToPendingChangesTypeMap[actionType];

  return mapped || PendingChangesActionType.MODIFIED;
};

export const getRelativeTime = dateString => {
  if (_.isNil(dateString)) {
    return '';
  }

  return dayjs(dateString).fromNow();
};

export const getExactDateTime = dateString => {
  if (_.isNil(dateString)) {
    return '';
  }

  const parsed = dayjs(dateString);

  if (!parsed.isValid()) {
    return '';
  }

  return parsed.format(COMMON_DATETIME_FORMAT);
};

const getSearchHaystack = change => {
  const fields = [
    change.tabName,
    change.rootEntityTypeName,
    change.propertyName,
    normalizeLoanChangesValue(change.oldValue),
    normalizeLoanChangesValue(change.newValue),
  ];

  return fields.reduce((acc, value) => {
    if (_.isNil(value) || value === '') {
      return acc;
    }

    const lowered = String(value).toLowerCase();

    return acc.length ? `${acc} ${lowered}` : lowered;
  }, '');
};

export const filterChangesBySearch = (changes, searchQuery) => {
  const trimmedQuery = (searchQuery || '').trim().toLowerCase();

  if (!trimmedQuery.length) {
    return changes;
  }

  return changes.filter(change => getSearchHaystack(change).includes(trimmedQuery));
};

const getRootEntityType = change => change.rootEntityTypeName || null;

export const groupChangesBySection = changes =>
  changes.reduce((acc, change) => {
    const section = change.tabName || 'Other';
    const existing = acc[section] || [];

    return { ...acc, [section]: [...existing, change] };
  }, {});

export const getSectionWithSubGroups = sectionChanges => {
  const grouped = sectionChanges.reduce((acc, change) => {
    const groupKey = getRootEntityType(change) || '_ungrouped';
    const existing = acc[groupKey] || [];

    return { ...acc, [groupKey]: [...existing, change] };
  }, {});

  const distinctKeys = Object.keys(grouped);
  const hasMultipleEntityTypes = distinctKeys.length > 1;

  if (!hasMultipleEntityTypes) {
    return [{ name: null, count: sectionChanges.length, changes: sectionChanges }];
  }

  return Object.entries(grouped)
    .map(([name, subChanges]) => ({
      name: name === '_ungrouped' ? 'Other' : name,
      count: subChanges.length,
      changes: subChanges,
    }))
    .sort((a, b) => b.count - a.count);
};

export const getSectionSummary = sectionChanges => {
  const uniqueUsers = _.uniqBy(sectionChanges, 'formattedFullName');

  return {
    changeCount: sectionChanges.length,
    userCount: uniqueUsers.length,
    users: uniqueUsers.map(({ formattedFullName, userName }) => ({
      formattedFullName,
      userName,
    })),
  };
};

export const getUniqueAuthors = changes =>
  _.uniqBy(
    changes.map(({ formattedFullName, userName }) => ({
      key: formattedFullName,
      formattedFullName,
      userName,
    })),
    'formattedFullName',
  );

export const buildDrawerAuthorsFromPendingChangesAuthors = (authors, currentUserLabel) =>
  authors.map(author => ({
    key: author.key,
    formattedFullName: author.isCurrentUser
      ? currentUserLabel
      : getPendingChangesAuthorLabel(author),
  }));

export const labelAiPendingChanges = (changes, authors) => {
  const aiAuthor = authors.find(({ isAi }) => isAi);

  if (!aiAuthor) {
    return changes;
  }

  const aiLabel = getPendingChangesAuthorLabel(aiAuthor);

  return changes.map(change =>
    change.authorKey === aiAuthor.key ? { ...change, formattedFullName: aiLabel } : change,
  );
};

// Changes annotated with their author (pending changes by user) are keyed by the author; anything
// else, such as loan action history, by the name it was recorded under.
export const getDrawerAuthorKey = change => change.authorKey ?? change.formattedFullName;

export const filterChangesByAuthorKeys = (changes, authorKeys) =>
  changes.filter(change => authorKeys.includes(getDrawerAuthorKey(change)));

// Log History lists every author who changed the loan, labelled like the pending changes menu: the
// current user as Me and AI changes as one AI Assistant author.
export const buildDrawerAuthorsFromChanges = (changes, { currentUserId, currentUserLabel }) =>
  _.uniqBy(
    changes.map(change => {
      const key = getDrawerAuthorKey(change);

      return {
        key,
        formattedFullName: key === currentUserId ? currentUserLabel : change.formattedFullName,
      };
    }),
    'key',
  );

export const labelAiChanges = changes =>
  changes.map(change =>
    change.authorKey === AI_PENDING_CHANGES_AUTHOR_KEY
      ? { ...change, formattedFullName: AI_ASSISTANT_AUTHOR_NAME }
      : change,
  );

export const scrollToField = fieldElement => {
  fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  fieldElement.classList.add('pulse-highlight');
  setTimeout(() => {
    fieldElement.classList.remove('pulse-highlight');
  }, PULSE_HIGHLIGHT_DURATION_MS);
};

export const pollForElement = (selector, callback) => {
  const attemptCounter = { count: 0 };

  const intervalId = setInterval(() => {
    const element = document.querySelector(selector);
    attemptCounter.count += 1;

    if (element) {
      clearInterval(intervalId);
      callback(element);

      return;
    }

    if (attemptCounter.count >= POLL_MAX_ATTEMPTS) {
      clearInterval(intervalId);
    }
  }, POLL_INTERVAL_MS);
};

export const filterChangesByAuthor = (changes, selectedAuthor) => {
  if (_.isNil(selectedAuthor)) {
    return changes;
  }

  return changes.filter(change => change.formattedFullName === selectedAuthor);
};

const filterChangesBySubGroup = (changes, sectionName, subGroupName) =>
  changes.filter(change => {
    if ((change.tabName || 'Other') !== sectionName) {
      return false;
    }

    const entityType = getRootEntityType(change) || 'Other';

    return entityType === subGroupName;
  });

export const filterChangesByTreeSelection = (changes, selectedSection, allSectionsKey) => {
  if (_.isNil(selectedSection) || selectedSection === allSectionsKey) {
    return changes;
  }

  if (selectedSection.startsWith(SUBGROUP_PREFIX)) {
    const withoutPrefix = selectedSection.slice(SUBGROUP_PREFIX.length);
    const separatorIndex = withoutPrefix.indexOf(':');
    const sectionName = withoutPrefix.slice(0, separatorIndex);
    const subGroupName = withoutPrefix.slice(separatorIndex + 1);

    return filterChangesBySubGroup(changes, sectionName, subGroupName);
  }

  if (selectedSection.startsWith(SECTION_PREFIX)) {
    const sectionName = selectedSection.slice(SECTION_PREFIX.length);

    return changes.filter(change => (change.tabName || 'Other') === sectionName);
  }

  return changes.filter(change => (change.tabName || 'Other') === selectedSection);
};

// A save writes only what it recalculates, so a save-written row is recalculated too — but only once nesting
// recorded a change kind. History saved before nesting carries no change kind; those rows are plain saved
// values, not recalculations, and must stay flat (FH-3769).
export const isRecalculatedChange = change =>
  (change.isWrittenBySave && !_.isNil(change.changeKind)) ||
  change.changeKind === PendingChangeKind.RECALCULATED;

const hasCauseInformation = change => !_.isNil(change.operationId) && !_.isNil(change.changeKind);

export const checkHasCauseInformation = changes => changes.some(hasCauseInformation);

// A saved row groups by the save that applied it (appliedByOperationId), so every edit saved together
// forms one group dated when it was saved; a pending row has no applied operation yet and groups by the
// edit operation that made it. Changes recorded before nesting existed carry no operation and group alone.
const getGroupOperationId = change => change.appliedByOperationId ?? change.operationId;

const getGroupKey = change => {
  const operationId = getGroupOperationId(change);

  return _.isNil(operationId) ? `change:${change.id}` : `operation:${operationId}`;
};

// A save whose changes were all recalculated (the change behind them was undone, or a pricing event or
// loan action recalculated the loan) has no direct change; its values nest under a placeholder instead.
const toOperationGroup = ([key, groupChanges]) => {
  const directChanges = groupChanges.filter(change => !isRecalculatedChange(change));
  const affectedChanges = groupChanges.filter(isRecalculatedChange);
  const [firstChange] = directChanges.length ? directChanges : groupChanges;

  return {
    key,
    directChanges,
    affectedChanges,
    allAffectedChanges: affectedChanges,
    totalAffectedCount: affectedChanges.length,
    isRecalculatedOnly: !directChanges.length,
    isWrittenBySave: groupChanges.some(change => change.isWrittenBySave),
    tabName: firstChange.tabName,
    authorKey: getDrawerAuthorKey(firstChange),
    formattedFullName: firstChange.formattedFullName,
    modifiedOn: _.maxBy(groupChanges, 'modifiedOnRaw')?.modifiedOn ?? firstChange.modifiedOn,
    latestModifiedOnRaw: _.max(groupChanges.map(change => change.modifiedOnRaw)) ?? 0,
  };
};

export const buildOperationGroups = changes =>
  _.orderBy(
    Object.entries(_.groupBy(changes, getGroupKey)).map(toOperationGroup),
    ['latestModifiedOnRaw'],
    ['desc'],
  );

// The author and section filters match a group by its own changes; a recalculated-only group has none,
// so they match its recalculated values instead.
export const filterOperationGroups = (groups, filterChanges) =>
  groups.reduce((acc, group) => {
    if (group.isRecalculatedOnly) {
      const affectedChanges = filterChanges(group.affectedChanges);

      if (affectedChanges.length) {
        acc.push({ ...group, affectedChanges });
      }

      return acc;
    }

    const directChanges = filterChanges(group.directChanges);

    if (directChanges.length) {
      acc.push({ ...group, directChanges });
    }

    return acc;
  }, []);

export const getDirectChanges = groups => groups.flatMap(({ directChanges }) => directChanges);

export const getRecalculatedOnlyChanges = groups =>
  groups
    .filter(({ isRecalculatedOnly }) => isRecalculatedOnly)
    .flatMap(({ affectedChanges }) => affectedChanges);

// A search can name a recalculated value (LTV, P&I); the match then shows the change that caused it
// with only the recalculated values that match, so the searched value is not lost among the rest.
export const filterOperationGroupsBySearch = (groups, searchQuery) =>
  groups.reduce((acc, group) => {
    const matchingDirectChanges = filterChangesBySearch(group.directChanges, searchQuery);
    const matchingAffectedChanges = filterChangesBySearch(group.affectedChanges, searchQuery);
    const hasMatchingAffectedChange = matchingAffectedChanges.length > 0;

    if (matchingDirectChanges.length || hasMatchingAffectedChange) {
      acc.push({
        ...group,
        directChanges: hasMatchingAffectedChange ? group.directChanges : matchingDirectChanges,
        affectedChanges: hasMatchingAffectedChange
          ? matchingAffectedChanges
          : group.affectedChanges,
        isAffectedNarrowed:
          hasMatchingAffectedChange &&
          matchingAffectedChanges.length < group.affectedChanges.length,
      });
    }

    return acc;
  }, []);

const getRecalculatedChanges = groups => groups.flatMap(({ affectedChanges }) => affectedChanges);

// The left menu lists one kind of change, each under its own section: the direct changes, or every
// recalculated value, whether a direct change caused it or nothing did.
export const getMenuChangesByKind = (groups, searchQuery) => ({
  [ChangeKindFilter.DIRECT]: filterChangesBySearch(getDirectChanges(groups), searchQuery),
  [ChangeKindFilter.RECALCULATED]: filterChangesBySearch(
    getRecalculatedChanges(groups),
    searchQuery,
  ),
});

export const getChangeKindCounts = menuChangesByKind => _.mapValues(menuChangesByKind, 'length');

const getSectionName = change => change.tabName || 'Other';

const getSubGroupName = change => getRootEntityType(change) || 'Other';

const countChangesByKind = (menuChangesByKind, predicate) =>
  _.mapValues(menuChangesByKind, changes => changes.filter(predicate).length);

const orderByCounts = items =>
  _.orderBy(
    items,
    [
      ({ counts }) => counts[ChangeKindFilter.DIRECT],
      ({ counts }) => counts[ChangeKindFilter.RECALCULATED],
    ],
    ['desc', 'desc'],
  );

// One menu for both kinds: each section and sub-group counts its direct changes and its recalculated
// values, so a section that only holds recalculated values is still listed.
const buildSectionTree = menuChangesByKind =>
  orderByCounts(
    Object.entries(groupChangesBySection(Object.values(menuChangesByKind).flat())).map(
      ([sectionName, sectionChanges]) => {
        const subGroupNames = _.uniq(sectionChanges.map(getSubGroupName));
        const hasSubGroups = subGroupNames.length > 1;
        const isInSection = change => getSectionName(change) === sectionName;

        return {
          name: sectionName,
          counts: countChangesByKind(menuChangesByKind, isInSection),
          hasSubGroups,
          subGroups: hasSubGroups
            ? orderByCounts(
                subGroupNames.map(subGroupName => ({
                  name: subGroupName,
                  counts: countChangesByKind(
                    menuChangesByKind,
                    change => isInSection(change) && getSubGroupName(change) === subGroupName,
                  ),
                })),
              )
            : [],
        };
      },
    ),
  );

// Direct lists only the sections that hold direct changes; Recalculated lists every section, with both
// counts.
export const buildSectionTreesByKind = menuChangesByKind => ({
  [ChangeKindFilter.DIRECT]: buildSectionTree({
    [ChangeKindFilter.DIRECT]: menuChangesByKind[ChangeKindFilter.DIRECT],
    [ChangeKindFilter.RECALCULATED]: [],
  }),
  [ChangeKindFilter.RECALCULATED]: buildSectionTree(menuChangesByKind),
});

const getSubGroupKeys = ({ name, subGroups }) =>
  subGroups.map(subGroup => `${SUBGROUP_PREFIX}${name}:${subGroup.name}`);

const checkIsSectionInTree = (sections, selectedSection) =>
  selectedSection === ALL_SECTIONS_KEY ||
  sections.some(
    section =>
      `${SECTION_PREFIX}${section.name}` === selectedSection ||
      getSubGroupKeys(section).includes(selectedSection),
  );

export const getSectionKeptInTree = (sections, selectedSection) =>
  checkIsSectionInTree(sections, selectedSection) ? selectedSection : ALL_SECTIONS_KEY;

// Under Recalculated a section shows everything in it: its direct changes, and its recalculated values
// under the change that caused them, that change shown even when it lives in another section.
const sliceGroupWithRecalculated = (group, filterChanges) => {
  const directChanges = filterChanges(group.directChanges);
  const affectedChanges = filterChanges(group.affectedChanges);

  if (!directChanges.length && !affectedChanges.length) {
    return null;
  }

  return {
    ...group,
    directChanges: directChanges.length ? directChanges : group.directChanges,
    affectedChanges,
    isAffectedNarrowed:
      !!affectedChanges.length && affectedChanges.length < group.totalAffectedCount,
  };
};

// Under Direct a group is matched by its direct changes, so a save that only recalculated values has no
// section of its own.
export const filterOperationGroupsBySection = (groups, selectedSection, changeKindFilter) => {
  if (selectedSection === ALL_SECTIONS_KEY) {
    return groups;
  }

  const filterChanges = changes =>
    filterChangesByTreeSelection(changes, selectedSection, ALL_SECTIONS_KEY);

  if (changeKindFilter === ChangeKindFilter.RECALCULATED) {
    return groups.map(group => sliceGroupWithRecalculated(group, filterChanges)).filter(Boolean);
  }

  return filterOperationGroups(
    groups.filter(({ isRecalculatedOnly }) => !isRecalculatedOnly),
    filterChanges,
  );
};
